import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../../api/client';
import { useUIStore } from '../../stores/uiStore';
import { useSSEStore } from '../../stores/sseStore';
import { StatusBadge } from '../../components/shared/StatusBadge';
import {
  ArrowLeft, ExternalLink, Package, RefreshCw, Eye,
  DollarSign, BarChart3, Layers, Settings, Edit3,
  TrendingUp, Database, ShoppingCart, Globe, Activity, FileText
} from 'lucide-react';
import SitesTab from './tabs/SitesTab';
import StockTab from './tabs/StockTab';
import PricingTab from './tabs/PricingTab';
import PurchasesTab from './tabs/PurchasesTab';
import EditMLTab from './tabs/EditMLTab';
import PerformanceTab from './tabs/PerformanceTab';
import MetadataTab from './tabs/MetadataTab';
import { useCan, visibleTabs, pickActiveTab, NoAccess, Can } from '../../lib/rbac';

interface SiteItem {
  site_item_id: string;
  site_id: string;
  title: string;
  price: number;
  currency_id: string;
  status: string;
  available_quantity: number;
  sold_quantity: number;
  net_proceeds: number | null;
  sale_fee: number | null;
  shipping_cost: number | null;
  permalink: string | null;
}

interface ProductDetail {
  item_id: string;
  seller_id: number;
  title: string;
  price: number;
  currency_id: string;
  status: string;
  asin: string;
  sku: string | null;
  thumbnail: string;
  secure_thumbnail: string;
  available_quantity: number;
  sold_quantity: number;
  brand: string;
  model: string;
  manual_stock: boolean;
  markup_override: boolean;
  manual_pricing: boolean;
  automation_enabled: boolean | null;
  has_automation: boolean;
  markup_type: string;
  markup_value: number;
  item_amazon_price: number;
  date_created: string;
  last_updated: string;
  permalink: string;
  amazon_price: number | null;
  amazon_image: string | null;
  amazon_brand: string | null;
  amazon_category: string | null;
  amazon_available: boolean | null;
  amazon_rating: number | null;
  amazon_ratings_total: number | null;
  keepa_monthly_sold: number | null;
  keepa_availability_amazon: number | null;
  weight_kg: number | null;
  dimensions_cm: string | null;
  margin_pct: number | null;
  site_count: number;
  category_id: string | null;
  domain_id: string | null;
  gtin: string | null;
  condition: string | null;
  seller_custom_field: string | null;
  source_type: string | null;
  cost_source: string | null;
  internal_cost: number | null;
  stock_source_type: string | null;
  internal_sku: string | null;
  internal_qty_on_hand: number | null;
  supplier_name: string | null;
  moderation_flags?: Record<string, any> | null;
  site_items?: SiteItem[];
}

const STATUS_MAP: Record<string, 'ok' | 'warning' | 'error' | 'info' | 'unknown'> = {
  active: 'ok',
  paused: 'warning',
  under_review: 'info',
  closed: 'error',
};

const TABS = [
  { key: 'general', label: 'General', icon: Eye },
  { key: 'edit', label: 'Editar ML', icon: Edit3 },
  { key: 'pricing', label: 'Pricing', icon: DollarSign },
  { key: 'stock', label: 'Stock', icon: Package },
  { key: 'purchases', label: 'Compras', icon: ShoppingCart },
  { key: 'sites', label: 'Sites', icon: Globe },
  { key: 'performance', label: 'Performance', icon: Activity },
  { key: 'metadata', label: 'Metadata', icon: FileText },
];

function ToggleSwitch({ enabled, onChange, loading, disabled }: { enabled: boolean; onChange: () => void; loading: boolean; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={onChange}
      disabled={loading || disabled}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-none ${
        enabled ? 'bg-[var(--color-primary)]' : 'bg-[var(--color-border)]'
      } ${loading || disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
          enabled ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

export default function CatalogDetailPage() {
  const { itemId } = useParams<{ itemId: string }>();
  const navigate = useNavigate();
  const { sellerId } = useUIStore();
  const { subscribe } = useSSEStore();

  const [product, setProduct] = useState<ProductDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [togglingField, setTogglingField] = useState<string | null>(null);
  const can = useCan();

  const allowedTabs = visibleTabs('catalogDetail', TABS, can);
  const active = pickActiveTab('catalogDetail', TABS, activeTab, can);
  useEffect(() => {
    if (active && active !== activeTab) setActiveTab(active);
  }, [active, activeTab]);

  const loadProduct = useCallback(async () => {
    if (!itemId) return;
    const res = await api.get<ProductDetail>(`/catalog/products/${itemId}?seller_id=${sellerId}`);
    if (res.success && res.data) {
      setProduct(res.data);
    }
    setLoading(false);
    setRefreshing(false);
  }, [itemId, sellerId]);

  useEffect(() => {
    setLoading(true);
    loadProduct();
  }, [loadProduct]);

  // SSE subscriptions for real-time updates
  useEffect(() => {
    const unsub1 = subscribe('stock:updated', (data) => {
      if (data.item_id === itemId) loadProduct();
    });
    const unsub2 = subscribe('stock:adjusted', (data) => {
      if (data.item_id === itemId) loadProduct();
    });
    return () => { unsub1(); unsub2(); };
  }, [subscribe, itemId, loadProduct]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadProduct();
  };

  const handleToggle = async (field: string, currentValue: boolean) => {
    if (!itemId) return;
    setTogglingField(field);
    try {
      await api.put(`/catalog/products/${itemId}/toggles?seller_id=${sellerId}`, { [field]: !currentValue });
      await loadProduct();
    } finally {
      setTogglingField(null);
    }
  };

  if (active === null) return <NoAccess />;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-3 border-[var(--color-primary)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="p-6">
        <button onClick={() => navigate('/catalog')} className="flex items-center gap-2 text-[var(--color-text-muted)] hover:text-[var(--color-text)] mb-4">
          <ArrowLeft size={20} />
          <span>Volver al catálogo</span>
        </button>
        <div className="text-center py-12 text-[var(--color-text-muted)]">Producto no encontrado</div>
      </div>
    );
  }

  const imgSrc = product.secure_thumbnail || product.thumbnail || product.amazon_image;

  return (
    <div className="p-4 md:p-6 space-y-4">
      {/* Header */}
      <div className="bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] p-4">
        <div className="flex items-start gap-4">
          <button
            onClick={() => navigate('/catalog')}
            className="mt-1 p-1.5 rounded-lg hover:bg-[var(--color-surface-hover)] text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors"
          >
            <ArrowLeft size={20} />
          </button>

          {imgSrc && (
            <img
              src={imgSrc}
              alt=""
              className="w-16 h-16 object-contain rounded-lg border border-[var(--color-border-light)] bg-white flex-shrink-0"
            />
          )}

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-lg font-semibold text-[var(--color-text)] leading-tight">{product.title}</h1>
              <StatusBadge status={STATUS_MAP[product.status] || 'unknown'} label={product.status} size="sm" />
            </div>

            <div className="flex items-center gap-3 mt-1.5 text-sm text-[var(--color-text-muted)] flex-wrap">
              {product.asin && (
                <a
                  href={`https://www.amazon.com/dp/${product.asin}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 hover:text-[var(--color-primary)] transition-colors"
                >
                  ASIN: {product.asin} <ExternalLink size={12} />
                </a>
              )}
              {product.sku && <span>SKU: {product.sku}</span>}
              <span>ID: {product.item_id}</span>
              {product.permalink && (
                <a
                  href={product.permalink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 hover:text-[var(--color-primary)] transition-colors"
                >
                  Ver en ML <ExternalLink size={12} />
                </a>
              )}
            </div>
          </div>

          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="p-2 rounded-lg hover:bg-[var(--color-surface-hover)] text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors flex-shrink-0"
          >
            <RefreshCw size={18} className={refreshing ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Brand Protection Banner */}
      {product?.moderation_flags?.brand_protection && (
        <div className="mx-4 mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
          <span className="text-amber-600 text-lg mt-0.5">⚠️</span>
          <div>
            <p className="text-sm font-semibold text-amber-800">Brand Protection</p>
            <p className="text-xs text-amber-700 mt-0.5">
              Este item tiene protección de marca activa.
              CBT: <span className="font-mono">{product.item_id}</span> | Site: <span className="font-mono">{product.moderation_flags.brand_protection.site_item_id}</span>
            </p>
            <p className="text-xs text-amber-600 mt-1">Requiere acreditación manual en MercadoLibre.</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)]">
        <div className="flex border-b border-[var(--color-border)] overflow-x-auto">
          {allowedTabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                  active === tab.key
                    ? 'border-[var(--color-primary)] text-[var(--color-primary)]'
                    : 'border-transparent text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:border-[var(--color-border)]'
                }`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className="p-4">
          {active === 'general' && (
            <GeneralTab
              product={product}
              sellerId={sellerId}
              togglingField={togglingField}
              onToggle={handleToggle}
              onRefresh={loadProduct}
            />
          )}
          {active === 'stock' && (
            <StockTab product={product} sellerId={sellerId} onRefresh={loadProduct} />
          )}
          {active === 'sites' && (
            <SitesTab product={product} sellerId={sellerId} onRefresh={loadProduct} />
          )}
          {active === 'pricing' && (
            <PricingTab product={product} sellerId={sellerId} onRefresh={loadProduct} />
          )}
          {active === 'purchases' && (
            <PurchasesTab product={product} sellerId={sellerId} />
          )}
          {active === 'edit' && (
            <EditMLTab product={product} sellerId={sellerId} onRefresh={loadProduct} />
          )}
          {active === 'performance' && (
            <PerformanceTab product={product} sellerId={sellerId} />
          )}
          {active === 'metadata' && (
            <MetadataTab product={product} sellerId={sellerId} />
          )}
          {active !== 'general' && active !== 'stock' && active !== 'sites' && active !== 'pricing' && active !== 'purchases' && active !== 'edit' && active !== 'performance' && active !== 'metadata' && (
            <div className="text-center py-12 text-[var(--color-text-muted)]">
              Implementar en siguiente fase
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function GeneralTab({
  product,
  sellerId,
  togglingField,
  onToggle,
  onRefresh,
}: {
  product: ProductDetail;
  sellerId: number;
  togglingField: string | null;
  onToggle: (field: string, currentValue: boolean) => void;
  onRefresh: () => void;
}) {
  const can = useCan();
  const canMoney = can('global.view_money');
  const [sourceType, setSourceType] = useState(product.source_type || product.cost_source || 'amazon');
  const [savingSource, setSavingSource] = useState(false);
  const currentSource = product.source_type || product.cost_source || 'amazon';
  const sourceChanged = sourceType !== currentSource;

  const handleSaveSource = async () => {
    setSavingSource(true);
    try {
      const res = await api.put(`/catalog/products/${product.item_id}/source?seller_id=${sellerId}`, {
        source_type: sourceType,
      });
      if (res.success) onRefresh();
    } catch (err) {
      console.error('Failed to update source type:', err);
    } finally {
      setSavingSource(false);
    }
  };

  const toggles = [
    {
      field: 'markup_override',
      label: 'Markup Override',
      description: 'Usar markup personalizado en lugar de reglas globales',
      value: product.markup_override,
    },
    {
      field: 'manual_pricing',
      label: 'Pricing Manual',
      description: 'Desactivar repricing automático del Price Monitor',
      value: product.manual_pricing,
    },
    {
      field: 'manual_stock',
      label: 'Stock Manual',
      description: 'Gestionar stock manualmente, sin sincronización automática con Amazon',
      value: product.manual_stock,
    },
    {
      field: 'automation_enabled',
      label: 'ML Pricing Automation',
      description: product.manual_pricing
        ? 'Bloqueado: Pricing Manual está activo (tiene prioridad sobre la automation)'
        : product.has_automation
          ? 'Activa: ML ajusta precio dentro de los bounds calculados desde markup rule'
          : 'Opt-in explícito para que el item entre en automation cuando sea elegible',
      value: product.automation_enabled === true,
      disabled: product.manual_pricing,
    },
  ];

  const metrics = [
    { label: 'Precio ML', value: `$${product.price?.toFixed(2) || '0.00'}`, sub: product.currency_id, icon: DollarSign, color: 'var(--color-primary)' },
    { label: 'Precio Amazon', value: canMoney && product.amazon_price ? `$${product.amazon_price.toFixed(2)}` : '—', sub: canMoney ? 'USD' : null, icon: TrendingUp, color: 'var(--color-warning)' },
    { label: 'Margen', value: canMoney && product.margin_pct != null ? `${product.margin_pct.toFixed(1)}%` : '—', sub: null, icon: BarChart3, color: canMoney && product.margin_pct != null && product.margin_pct < 0 ? 'var(--color-error)' : 'var(--color-success)' },
    { label: 'Stock', value: String(product.available_quantity ?? 0), sub: null, icon: Package, color: 'var(--color-primary)' },
    { label: 'Vendidos', value: String(product.sold_quantity ?? 0), sub: null, icon: ShoppingCart, color: 'var(--color-success)' },
    { label: 'Sites', value: String(product.site_count ?? 0), sub: null, icon: Globe, color: 'var(--color-primary)' },
  ];

  const infoFields = [
    { label: 'Categoría', value: product.category_id },
    { label: 'Domain', value: product.domain_id },
    { label: 'Brand', value: product.brand || product.amazon_brand },
    { label: 'Model', value: product.model },
    { label: 'GTIN', value: product.gtin },
    { label: 'Condition', value: product.condition },
    { label: 'Creado', value: product.date_created ? new Date(product.date_created).toLocaleDateString() : null },
    { label: 'Actualizado', value: product.last_updated ? new Date(product.last_updated).toLocaleDateString() : null },
    { label: 'Peso', value: product.weight_kg ? `${product.weight_kg} kg` : null },
    { label: 'Dimensiones', value: product.dimensions_cm },
  ];

  return (
    <div className="space-y-6">
      {/* Toggle Controls — solo con la llave de los interruptores */}
      <Can cap="catalog.toggles">
      <div>
        <h3 className="text-sm font-semibold text-[var(--color-text)] mb-3 flex items-center gap-2">
          <Settings size={16} />
          Controles
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {toggles.map((t) => (
            <div
              key={t.field}
              className="flex items-center justify-between p-3 rounded-lg border border-[var(--color-border-light)] bg-[var(--color-bg)]"
            >
              <div className="min-w-0 mr-3">
                <div className="text-sm font-medium text-[var(--color-text)]">{t.label}</div>
                <div className="text-xs text-[var(--color-text-muted)] mt-0.5">{t.description}</div>
              </div>
              <ToggleSwitch
                enabled={t.value}
                onChange={() => !t.disabled && onToggle(t.field, t.value)}
                loading={togglingField === t.field}
                disabled={t.disabled}
              />
            </div>
          ))}
        </div>
      </div>
      </Can>

      {/* Source Type — misma llave que los interruptores */}
      <Can cap="catalog.toggles">
      <div>
        <h3 className="text-sm font-semibold text-[var(--color-text)] mb-3 flex items-center gap-2">
          <Layers size={16} />
          Fuente del Producto
        </h3>
        <div className="flex items-center gap-3 p-3 rounded-lg border border-[var(--color-border-light)] bg-[var(--color-bg)]">
          <div className="min-w-0">
            <div className="text-sm font-medium text-[var(--color-text)]">Source Type</div>
            <div className="text-xs text-[var(--color-text-muted)] mt-0.5">Proveedor de costo para este item</div>
          </div>
          <select
            value={sourceType}
            onChange={(e) => setSourceType(e.target.value)}
            className="ml-auto px-3 py-1.5 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-secondary)] text-sm text-[var(--color-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
          >
            <option value="amazon">Amazon</option>
            <option value="internal">Internal</option>
          </select>
          {sourceChanged && (
            <button
              onClick={handleSaveSource}
              disabled={savingSource}
              className="px-3 py-1.5 rounded-md text-sm font-medium text-white bg-[var(--color-primary)] hover:opacity-90 disabled:opacity-50"
            >
              {savingSource ? 'Guardando...' : 'Guardar'}
            </button>
          )}
        </div>
      </div>
      </Can>

      {/* Metrics Cards */}
      <div>
        <h3 className="text-sm font-semibold text-[var(--color-text)] mb-3 flex items-center gap-2">
          <BarChart3 size={16} />
          Métricas
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {metrics.map((m) => {
            const Icon = m.icon;
            return (
              <div key={m.label} className="p-3 rounded-lg border border-[var(--color-border-light)] bg-[var(--color-bg)]">
                <div className="flex items-center gap-1.5 mb-1">
                  <Icon size={14} style={{ color: m.color }} />
                  <span className="text-xs text-[var(--color-text-muted)]">{m.label}</span>
                </div>
                <div className="text-lg font-semibold text-[var(--color-text)]">{m.value}</div>
                {m.sub && <div className="text-xs text-[var(--color-text-muted)]">{m.sub}</div>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Product Info Grid */}
      <div>
        <h3 className="text-sm font-semibold text-[var(--color-text)] mb-3 flex items-center gap-2">
          <Database size={16} />
          Información del Producto
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {infoFields.map((f) => (
            <div key={f.label} className="p-3 rounded-lg border border-[var(--color-border-light)] bg-[var(--color-bg)]">
              <div className="text-xs text-[var(--color-text-muted)] mb-0.5">{f.label}</div>
              <div className="text-sm font-medium text-[var(--color-text)] truncate" title={f.value || undefined}>
                {f.value || '—'}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Cost Source Info */}
      {product.cost_source === 'internal_stock' && (
        <div>
          <h3 className="text-sm font-semibold text-[var(--color-text)] mb-3 flex items-center gap-2">
            <Layers size={16} />
            Stock Interno
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'SKU Interno', value: product.internal_sku },
              { label: 'Costo', value: canMoney && product.internal_cost != null ? `$${product.internal_cost.toFixed(2)}` : null },
              { label: 'Disponible', value: product.internal_qty_on_hand != null ? String(product.internal_qty_on_hand) : null },
              { label: 'Proveedor', value: canMoney ? product.supplier_name : null },
            ].map((f) => (
              <div key={f.label} className="p-3 rounded-lg border border-[var(--color-border-light)] bg-[var(--color-bg)]">
                <div className="text-xs text-[var(--color-text-muted)] mb-0.5">{f.label}</div>
                <div className="text-sm font-medium text-[var(--color-text)]">{f.value || '—'}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Variations */}
      {(product as any).variations?.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold text-[var(--color-text)] mb-3 flex items-center gap-2">
            <Layers size={16} />
            Variaciones ({(product as any).variations.length})
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-[var(--color-text-muted)] border-b border-[var(--color-border-light)]">
                  <th className="pb-2 pr-3">Color/Size</th>
                  <th className="pb-2 pr-3">ASIN</th>
                  <th className="pb-2 pr-3 text-right">Precio ML</th>
                  <th className="pb-2 pr-3 text-right">Precio Amazon</th>
                  <th className="pb-2 pr-3 text-right">Stock</th>
                  <th className="pb-2 pr-3 text-right">Vendidos</th>
                  <th className="pb-2">SKU</th>
                </tr>
              </thead>
              <tbody>
                {(product as any).variations.map((v: any) => (
                  <tr key={v.variation_id} className="border-b border-[var(--color-border-light)] hover:bg-[var(--color-bg-hover)]">
                    <td className="py-2 pr-3 font-medium text-[var(--color-text)]">
                      {[v.color, v.size].filter(Boolean).join(' / ') || '—'}
                    </td>
                    <td className="py-2 pr-3 text-[var(--color-text-muted)] font-mono text-xs">{v.asin || '—'}</td>
                    <td className="py-2 pr-3 text-right text-[var(--color-text)]">
                      {v.price != null ? `$${Number(v.price).toFixed(2)}` : '—'}
                    </td>
                    <td className="py-2 pr-3 text-right">
                      {canMoney && v.amazon_price != null
                        ? <span className="text-green-400">${Number(v.amazon_price).toFixed(2)}</span>
                        : <span className="text-[var(--color-text-muted)]">—</span>
                      }
                    </td>
                    <td className="py-2 pr-3 text-right text-[var(--color-text)]">{v.available_quantity ?? '—'}</td>
                    <td className="py-2 pr-3 text-right text-[var(--color-text-muted)]">{v.sold_quantity ?? 0}</td>
                    <td className="py-2 text-[var(--color-text-muted)] text-xs">{v.seller_sku || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
